import {
  applyRedactedOverrides,
  getRedactedOrNodeOrFallback,
} from "../lib/i18n-helpers";
import { ComponentPath, Language, langToLocale } from "../lib/i18n-registry";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { AuthContext } from "./auth";
import { loadSingleTranslation } from "../lib/load-i18n";

export { getRedactedOrNodeOrFallback };

type I18nContextType = {
  loadTranslations: (
    path: ComponentPath,
    section: string,
  ) => Promise<Record<string, any>>;
  getI18nValue: (
    i18n: Record<string, any>,
    node: string,
    fallback?: any,
  ) => any;
  lang: Language;
  defineLang: (lang: Language) => void;
};

// Context to store the user
export const I18nContext = createContext<I18nContextType>({
  loadTranslations: async (path: ComponentPath, section: string) => ({}),
  getI18nValue: (_i18n, _node, fallback) => fallback,
  lang: "en",
  defineLang: (lang: Language) => {},
});

const I18N_LANG_STORAGE_KEY = "admin.i18n.lang";

const getStoredLanguage = (): Language | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const storedLang = window.localStorage.getItem(I18N_LANG_STORAGE_KEY);
  return storedLang ? (storedLang as Language) : null;
};

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useContext(AuthContext);
  const [lang, setLang] = useState<Language>(() => {
    const storedLang = getStoredLanguage();
    if (storedLang) {
      return storedLang;
    }

    return (user?.language as Language) || "en";
  });

  useEffect(() => {
    const storedLang = getStoredLanguage();
    if (!storedLang && user?.language && lang !== (user.language as Language)) {
      setLang(user.language as Language);
    }
  }, [user, lang]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    document.documentElement.lang = langToLocale(lang);
    window.localStorage.setItem(I18N_LANG_STORAGE_KEY, lang);
  }, [lang]);

  const loadTranslations = useCallback(
    async (path: ComponentPath, section: string) => {
      const values = await loadSingleTranslation(lang, path);
      const sectionValues = values[section] || {};
      return sectionValues;
    },
    [lang],
  );

  // Uses the current i18n state to resolve the value
  const getI18nValue = useCallback(
    (i18n: Record<string, any>, node: string, fallback: string = "") => {
      const resolved = getRedactedOrNodeOrFallback(i18n, node, fallback);
      return applyRedactedOverrides(resolved);
    },
    [],
  );

  const defineLang = (newLang: Language) => {
    setLang(newLang);
  };

  return (
    <I18nContext.Provider
      value={{ lang, loadTranslations, getI18nValue, defineLang }}
    >
      {children}
    </I18nContext.Provider>
  );
};
